import { muiTheme } from "@/styles/theme";
import {
  AssignmentTypes,
  CommentProps,
  CommentsSource,
  CommentsType,
} from "@/modules/lead-page/types";
import { OptionProps } from "@/modules/common/types";
import { LeadTypesDefaultUsersResponse } from "@/modules/lead-page/dtos";
import { GetLeadResponse, GetLeadsTypeResponse } from "@/modules/leads/dtos";
import { SaleDocumentType } from "@/modules/leads/constants";
import { SaleCategory } from "@/modules/common/constants";
import { FollowUpStatusEnum } from "@/modules/lead-page/enums";
import { BatchInfo } from "@/modules/lead-page/types";

export const formatComments = (
  filterLabel: CommentsType,
  comments?: CommentProps[],
) => {
  const commentProps: CommentProps[] = [];
  if (comments) {
    const formattedComments = comments.map((comment) => {
      let assignmentType;
      switch (comment.assignment_type) {
        case "Automatically":
          assignmentType = AssignmentTypes.AUTO_ASSIGNMENT;
          break;
        case "Manually":
          assignmentType = AssignmentTypes.MANUAL;
          break;
        case "with manual Multi-Selection":
          assignmentType = AssignmentTypes.MANUAL_MULTI_SELECTION;
          break;
        case "Rotation":
          assignmentType = AssignmentTypes.ROTATION;
          break;
        case "from pool":
          assignmentType = AssignmentTypes.POOL;
          break;
        case "Automatically due to Auto Assignment trigger":
          assignmentType = AssignmentTypes.AUTO_ASSIGNMENT_BUTTON;
          break;
        case "Delegation":
          assignmentType = AssignmentTypes.DELEGATION;
          break;
        case "through re-assign to manager button":
          assignmentType = AssignmentTypes.REASSIGNMENT_TO_MANAGER;
          break;
        default:
          assignmentType = "";
      }

      return {
        ...comment,
        message:
          comment.comment_type === CommentsType.ASSIGNMENT
            ? `Lead has been ${
                comment.new_assigned_user_name === undefined
                  ? "un-assigned"
                  : "assigned"
              } ${assignmentType}`
            : comment.message,
      };
    });

    const filteredComments =
      filterLabel === CommentsType.COMMENT
        ? formattedComments.filter(
            (comment) => comment.comment_type === CommentsType.COMMENT,
          )
        : filterLabel === CommentsType.STATUS
          ? formattedComments.filter(
              (comment) => comment.comment_type === CommentsType.STATUS,
            )
          : filterLabel === CommentsType.ASSIGNMENT
            ? formattedComments.filter(
                (comment) => comment.comment_type === CommentsType.ASSIGNMENT,
              )
            : formattedComments;

    commentProps.push(...filteredComments);
  }

  commentProps.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  return commentProps;
};

const commentColorMappings = {
  hiddenAndOldLead: {
    backrgroundColor: "#BFDFDF",
    type: "Hidden, Old Lead",
  },
  oldLeadOnly: {
    backrgroundColor: "#FFF4E0",
    type: "Old Lead",
  },
  hiddenOnly: {
    backrgroundColor: muiTheme.colorSchemes.light.palette.main.grey,
    type: "Deleted Comment",
  },
  owners: {
    backrgroundColor: "#F3F1FF",
    type: "Via Owners Portal",
  },
  default: {
    backrgroundColor: muiTheme.colorSchemes.light.palette.background.white,
    type: "",
  },
};

export const getCommentBackgroundColor = (comment: CommentProps) => {
  if (!comment) {
    return commentColorMappings.default;
  }

  if (
    comment.source === CommentsSource.OWNERS &&
    comment.comment_type === CommentsType.COMMENT &&
    comment.deletedAt === null
  ) {
    return commentColorMappings.owners;
  }

  if (
    comment.old_lead_id &&
    comment.deletedAt !== null &&
    comment.comment_type === CommentsType.COMMENT
  ) {
    return commentColorMappings.hiddenAndOldLead;
  }

  if (
    (comment.old_lead_id ||
      comment.lead_id?.toString() !== comment.current_lead_id?.toString()) &&
    comment.deletedAt === null
  ) {
    return commentColorMappings.oldLeadOnly;
  }

  if (
    comment.deletedAt !== null &&
    comment.comment_type === CommentsType.COMMENT
  ) {
    return commentColorMappings.hiddenOnly;
  }

  return commentColorMappings.default;
};

export const filterLeadTypes = (
  defaultUsersLeadTypes: LeadTypesDefaultUsersResponse[],
  leadTypes: GetLeadsTypeResponse[],
): GetLeadsTypeResponse[] => {
  const validLeadTypeIds = new Set(
    defaultUsersLeadTypes.map((type) => type.lead_type_id),
  );

  const filteredLeadTypes = leadTypes.filter((leadType) =>
    validLeadTypeIds.has(leadType.id),
  );

  return filteredLeadTypes.sort((a, b) => a.id - b.id);
};

export const isDate = (value: string): boolean => {
  const dateRegex =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}(Z|[+-]\d{2}:\d{2})$/;
  return dateRegex.test(value);
};

export const formatDate = (date: string, includeTime = true): string => {
  return new Date(date).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: includeTime ? "2-digit" : undefined,
    minute: includeTime ? "2-digit" : undefined,
    hour12: true,
  });
};

export const parseFormData = (data: any) => {
  let parsedComment = data.comment || "";
  let formDataComment = "";

  const appendToFormData = (line: string) => {
    formDataComment += line + "\n";
  };

  if (data.property_type) {
    appendToFormData(`property type : ${data.property_type.label}`);
  }

  if (data.location) {
    let locationString = "location: ";
    if (data.location.area) {
      locationString += `Area: ${data.location.area.label}`;
    }
    if (data.location.compounds && data.location.compounds.length > 0) {
      locationString += data.location.area ? ", " : "";
      locationString += `Compounds: ${data.location.compounds
        .map((compound: OptionProps) => compound.label)
        .join(", ")}`;
    }
    appendToFormData(locationString);
  }

  if (data.client_objective) {
    appendToFormData(`client objective : ${data.client_objective.label}`);
  }

  if (data.average_budget) {
    const budget = Number(data.average_budget.toString().replace(/,/g, ""));
    appendToFormData(`average budget : ${budget}`);
  }

  if (data.unit_area) {
    appendToFormData(`unit area : ${data.unit_area}`);
  }

  if (data.rooms) {
    appendToFormData(`rooms : ${data.rooms}`);
  }

  if (formDataComment) {
    parsedComment += parsedComment
      ? `\n\n Form Comment:-\n ${formDataComment.trim()}`
      : "";
  }

  return parsedComment.trim();
};

// Helper to format budget as currency
export const formatBudget = (budget: number | null) => {
  if (budget == null) return "—";
  return `EGP ${budget.toLocaleString("en-EG")}`;
};

// Helper to calculate validity status
export const getValidityStatus = (
  expirationDate: string | null,
  createdAt: string,
) => {
  if (expirationDate) {
    const now = new Date();
    const expiry = new Date(expirationDate);
    const diff = Math.ceil(
      (expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diff > 7) {
      return { text: `${diff} days`, color: "success", expired: false };
    } else if (diff > 0) {
      return { text: `${diff} days`, color: "warning", expired: false };
    } else {
      return { text: "Expired", color: "error", expired: true };
    }
  }

  // fallback to old logic if no expiration date
  const created = new Date(createdAt);
  const now = new Date();
  const diff = Math.max(
    0,
    Math.ceil((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)),
  );
  const validityDays = Math.max(0, 5 - diff);
  const expired = diff >= 5;

  return {
    text: `${validityDays} days`,
    color:
      validityDays > 2 ? "success" : validityDays > 0 ? "warning" : "error",
    expired,
  };
};

export const getDaysFromNow = (expirationDate: string | null) => {
  if (!expirationDate) return null;
  const now = new Date();
  const expiration = new Date(expirationDate);
  const diffTime = expiration.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export const getLeadPhoneNumberFromGetLeadResponse = (
  lead: GetLeadResponse,
): string | undefined => {
  return lead?.data?.lead?.lead_telephones?.length === 1
    ? lead?.data?.lead?.lead_telephones[0].telephone
    : lead?.data?.lead?.lead_telephones?.find(
        (telephone) => telephone.primary === true,
      )?.telephone;
};
export const handleSaleCategoryChange = (
  saleCategory: string | number,
  setSaleDocumentTypeOption: (saleDocumentType: SaleDocumentType) => void,
) => {
  switch (saleCategory) {
    // Payment Plan Documents
    case SaleCategory.PRIMARY:
    case SaleCategory.DEVELOPER_RESALE:
    case SaleCategory.DEVELOPER_COMMERCIAL_SALE:
      setSaleDocumentTypeOption(SaleDocumentType.PAYMENT_PLAN);
      break;
    // Payment Documents
    case SaleCategory.RESALE_BUYER:
    case SaleCategory.RESALE_SELLER:
    case SaleCategory.COMMERCIAL_LEASING:
    case SaleCategory.COMMERCIAL_SALE:
    case SaleCategory.NAYW_NOW:
    case SaleCategory.NAYW_NOW_REFINANCE:
      setSaleDocumentTypeOption(SaleDocumentType.PAYMENT);
      break;
  }
};

export const isFollowUpResolved = (
  followUpStatus?: FollowUpStatusEnum,
): boolean => {
  return (
    followUpStatus === FollowUpStatusEnum.RESOLVED ||
    followUpStatus === FollowUpStatusEnum.NO_FOLLOW_UP
  );
};

export const getBatchInfo = (
  links: Array<{ title?: string; link?: string }>,
  batchIndex: number,
  visibleBatchCount: number,
  batchSize: number,
): BatchInfo => {
  const startIndex = batchIndex * batchSize;
  const batchLinks = links.slice(startIndex, startIndex + batchSize);

  const isLastBatch = batchIndex === visibleBatchCount - 1;
  const remainingCount = links.length - (batchIndex + 1) * batchSize;

  return {
    batchLinks,
    isLastBatch,
    remainingCount,
  };
};

/**
 * Helper function to capitalize and format field labels
 * Examples:
 *   "developers" -> "Developers"
 *   "sale_types" -> "Sale Types"
 *   "installment_years" -> "Installment Years"
 */
export const formatFieldLabel = (key: string): string => {
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Type for erealty payload - key-value pairs where value can be anything
type ErealtyPayload = Record<string, any>;

export const transformErealtyPayload = (erealtyPayload: ErealtyPayload) => {
  const transformedData: Record<string, any> = {};
  const filtersWithIds: Record<string, { uid: string; label: string; value: any }> =
    erealtyPayload.filters_with_ids || {};

  Object.entries(erealtyPayload).forEach(([key, value]) => {
    // Exclude sale_types, filters_with_ids, and number_of_compounds from erealty payload
    if (
      key.toLowerCase() === "sale_types" ||
      key === "filters_with_ids" 
    ) {
      return;
    }

    const fieldLabel = formatFieldLabel(key);

    // Find matching filter entry by comparing label (case-insensitive) to the key
    const matchedFilter = Object.values(filtersWithIds).find(
      (filter) => filter.label.toLowerCase() === key.toLowerCase(),
    );
    const fieldUId = matchedFilter?.uid || "";

    let submittedData: Array<{ value: any; label: any }> = [];

    if (Array.isArray(value)) {
      submittedData = value.map((item) => {
        if (typeof item === "object" && item !== null) {
          const itemName = item.name ?? null;
          const itemValue = itemName ?? item.value ?? item.id ?? null;
          const itemLabel = itemName ?? item.label ?? null;
          return { value: itemValue, label: itemLabel };
        } else {
          return { value: item, label: null };
        }
      });
    } else if (typeof value === "object" && value !== null) {
      const itemName = value.name ?? null;
      const itemValue = itemName ?? value.value ?? value.id ?? null;
      const itemLabel = itemName ?? value.label ?? null;
      submittedData = [{ value: itemValue, label: itemLabel }];
    } else if (value !== null && value !== undefined) {
      submittedData = [{ value: value, label: null }];
    } else {
      transformedData[fieldLabel] = {
        fieldUId,
        field_label: fieldLabel,
        submitted_data: null,
      };
      return;
    }

    transformedData[fieldLabel] = {
      fieldUId,
      field_label: fieldLabel,
      submitted_data: submittedData,
    };
  });

  return transformedData;
};

export const transformFormData = (
  values: any,
  formFields: { data: any } | null | undefined,
  erealtyIframePayload?: any,
) => {
  if (erealtyIframePayload) {
    return transformErealtyPayload(erealtyIframePayload);
  }

  const formData = Object.entries(values)
    .filter(([key, value]) => key !== "recordings" && value)
    .reduce((acc: any, [key, value]: [string, any]) => {
      const fieldType =
        formFields?.data.fields.find((i: any) => i.field.key === key)?.field
          .type || "";

      acc[key] = {
        fieldUId:
          formFields?.data.fields.find((i: any) => i.field.key === key)?.field
            .uid || "",
        type: fieldType || "",
        field_label:
          formFields?.data.fields.find((i: any) => i.field.key === key)?.field
            .label || "",
        submitted_data: Array.isArray(value)
          ? value.map((v: any) => ({
              value: v.value,
              label: v.label,
            }))
          : [
              {
                value:
                  (value.value || value) && ["datetime"].includes(fieldType)
                    ? new Date(value.value || value).toString()
                    : value.value || value || "",
                label: value.label || null,
              },
            ],
      };
      return acc;
    }, {});

  return formData;
};
